import type { RankOrderAnswers } from '../../submitSurvey/types';

export function isRankOrderAnswers(value: unknown): value is RankOrderAnswers {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every(
    rank => typeof rank === 'number' && Number.isFinite(rank) && rank > 0
  );
}

export function normalizeRankOrderAnswers(value: unknown): RankOrderAnswers {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const normalized: RankOrderAnswers = {};

  for (const [optionId, rankValue] of Object.entries(value)) {
    const rank =
      typeof rankValue === 'number'
        ? rankValue
        : typeof rankValue === 'string' && rankValue.trim() !== ''
          ? Number(rankValue)
          : NaN;

    if (Number.isFinite(rank) && rank > 0) {
      normalized[optionId] = rank;
    }
  }

  return normalized;
}

export function getOptionRank(answers: RankOrderAnswers, optionId: string): number | undefined {
  const rank = answers[optionId];
  return typeof rank === 'number' && Number.isFinite(rank) ? rank : undefined;
}

export function isRankOrderAnswerEmpty(
  answers: RankOrderAnswers,
  visibleOptionIds: string[],
  requireRankAll: boolean
): boolean {
  if (visibleOptionIds.length === 0) {
    return true;
  }

  const rankedVisibleCount = visibleOptionIds.filter(optionId => getOptionRank(answers, optionId) !== undefined).length;

  if (requireRankAll) {
    return rankedVisibleCount < visibleOptionIds.length;
  }

  return rankedVisibleCount === 0;
}

export function hasDuplicateRanks(answers: RankOrderAnswers, visibleOptionIds: string[]): boolean {
  const seenRanks = new Set<number>();

  for (const optionId of visibleOptionIds) {
    const rank = getOptionRank(answers, optionId);
    if (rank === undefined) {
      continue;
    }

    if (seenRanks.has(rank)) {
      return true;
    }

    seenRanks.add(rank);
  }

  return false;
}

export function buildRankOrderFromOrderedOptionIds(optionIds: string[]): RankOrderAnswers {
  const answers: RankOrderAnswers = {};

  optionIds.forEach((optionId, index) => {
    answers[optionId] = index + 1;
  });

  return answers;
}

export function getOrderedOptionIdsFromRanks(
  answers: RankOrderAnswers,
  optionIds: string[]
): string[] {
  return [...optionIds]
    .filter(optionId => getOptionRank(answers, optionId) !== undefined)
    .sort((leftOptionId, rightOptionId) => {
      const leftRank = getOptionRank(answers, leftOptionId) ?? Number.MAX_SAFE_INTEGER;
      const rightRank = getOptionRank(answers, rightOptionId) ?? Number.MAX_SAFE_INTEGER;
      return leftRank - rightRank;
    });
}

export function assignRankWithoutDuplicates(
  currentAnswers: RankOrderAnswers,
  optionId: string,
  rank: number | undefined
): RankOrderAnswers {
  const nextAnswers = { ...currentAnswers };

  if (rank === undefined) {
    delete nextAnswers[optionId];
    return nextAnswers;
  }

  for (const [existingOptionId, existingRank] of Object.entries(nextAnswers)) {
    if (existingOptionId !== optionId && existingRank === rank) {
      delete nextAnswers[existingOptionId];
    }
  }

  nextAnswers[optionId] = rank;
  return nextAnswers;
}
